/**
 * SupportThemeProvider — D-33
 *
 * Wraps the Concierge chat area and exposes brand-controlled support-surface
 * branding to the six downstream surfaces:
 *
 *   - in-input "Connected to" header strip + Disconnect button
 *     (`ConciergeInputArea.tsx`)
 *   - sidebar pulsing-dot indicator on the active-takeover row
 *     (`ConciergeSidebar.tsx`)
 *   - human-reply bubble + agent-initials avatar + label
 *     (`HumanReplyPart.tsx`)
 *   - file-ticket card + ticket list pill + ticket status part
 *     (`SupportTicketCard.tsx`, `SupportTicketsListPart.tsx`,
 *      `SupportTicketStatusPart.tsx`)
 *   - per-message delivery-status row under user bubbles during takeover
 *     (`ChatMessage.tsx`)
 *
 * Two outputs:
 *
 * 1. Four CSS custom properties on a wrapper `<div>`. The surfaces consume
 *    them via `tw:bg-[var(--bc-support-bg)]` / `tw:border-[var(--bc-support-border)]`
 *    / `tw:bg-[var(--bc-support-dot)]` / `tw:text-[var(--bc-support-text)]`.
 *
 * 2. A sibling `SupportCopyContext` exposing the five copy fields + avatar URL
 *    via `useSupportCopy()`. Surfaces read this for `teamLabel`,
 *    `pausedFooterCopy`, etc. without prop-drilling through the chat-area
 *    props bag.
 *
 * Defaults are pinned to today's hardcoded emerald hexes + copy strings, so a
 * brand who never opens the new Concierge Studio Branding/Support tab is
 * pixel- and copy-identical to the pre-D-33 surface.
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useStudioSupportBranding, type StudioSupportBranding } from '@brandclub/bc-widgets';

interface SupportCopy {
  teamLabel: string;
  pausedFooterCopy: string;
  takeoverPlaceholder: string;
  deliveredCopy: string;
  teamAvatarUrl: string;
}

const FALLBACK_COPY: SupportCopy = {
  teamLabel: 'Support Team',
  pausedFooterCopy: "You're chatting with support team. Concierge is paused.",
  takeoverPlaceholder: 'Message {firstName}…',
  deliveredCopy: 'Delivered to Support Team',
  teamAvatarUrl: '',
};

const SupportCopyContext = createContext<SupportCopy>(FALLBACK_COPY);

export interface SupportThemeProviderProps {
  children: React.ReactNode;
  /** Optional className passthrough on the wrapper div. */
  className?: string;
}

export const SupportThemeProvider: React.FC<SupportThemeProviderProps> = ({
  children,
  className,
}) => {
  const branding: StudioSupportBranding = useStudioSupportBranding();

  const cssVars = useMemo<React.CSSProperties>(
    () => ({
      // `display: contents` makes this wrapper transparent for layout —
      // children participate in the parent's flex/grid context as if the
      // wrapper weren't there. CSS custom properties still cascade normally.
      display: 'contents',
      // Cast to any so React's CSSProperties accepts custom -- properties.
      ['--bc-support-bg' as any]: branding.softBackgroundHex,
      ['--bc-support-border' as any]: branding.borderHex,
      ['--bc-support-dot' as any]: branding.accentDotHex,
      ['--bc-support-text' as any]: branding.textHex,
    }),
    [branding.softBackgroundHex, branding.borderHex, branding.accentDotHex, branding.textHex],
  );

  const copy = useMemo<SupportCopy>(
    () => ({
      teamLabel: branding.teamLabel,
      pausedFooterCopy: branding.pausedFooterCopy,
      takeoverPlaceholder: branding.takeoverPlaceholder,
      deliveredCopy: branding.deliveredCopy,
      teamAvatarUrl: branding.teamAvatarUrl,
    }),
    [
      branding.teamLabel,
      branding.pausedFooterCopy,
      branding.takeoverPlaceholder,
      branding.deliveredCopy,
      branding.teamAvatarUrl,
    ],
  );

  return (
    <div className={className} style={cssVars}>
      <SupportCopyContext.Provider value={copy}>{children}</SupportCopyContext.Provider>
    </div>
  );
};

/**
 * Read the brand-controlled support-surface copy strings + avatar URL.
 * Always returns the full shape; fields the brand hasn't customized fall back
 * to today's defaults.
 *
 * Safe to call from any component rendered inside `<SupportThemeProvider>`;
 * callers outside the provider get the default copy (no null guards needed).
 */
export const useSupportCopy = (): SupportCopy => useContext(SupportCopyContext);
